import { Box, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";

import { useEffect, useState } from "react";
import toast, { Toast, Toaster } from "react-hot-toast";
import { useStyles } from "./index.styles";

export type ToastKind = "success" | "info" | "error";

const STAMPS: Record<ToastKind, string> = {
  success: "Success",
  info: "Notice",
  error: "Failed",
};

interface AppToastProps {
  t: Toast;
  kind: ToastKind;
  message: string;
  detail?: string;
}

const AppToast = ({ t, kind, message, detail }: AppToastProps) => {
  const { classes, cx } = useStyles();
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!t.visible) {
      setEntered(false);
      return;
    }

    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setEntered(true));
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [t.visible]);

  return (
    <Box
      className={cx(
        classes.toast,
        entered && t.visible && classes.entered,
        !t.visible && classes.leaving
      )}
      role="status"
    >
      <Box
        className={cx(
          classes.rail,
          kind === "success" && classes.successRail,
          kind === "info" && classes.infoRail,
          kind === "error" && classes.errorRail
        )}
      />
      <Box className={classes.body}>
        <Typography
          className={cx(
            classes.stamp,
            kind === "success" && classes.successStamp,
            kind === "info" && classes.infoStamp,
            kind === "error" && classes.errorStamp
          )}
        >
          {STAMPS[kind]}
        </Typography>
        <Typography className={classes.message}>{message}</Typography>
        {detail ? (
          <Typography className={classes.detail}>{detail}</Typography>
        ) : null}
      </Box>
      <IconButton
        className={classes.close}
        onClick={() => toast.dismiss(t.id)}
        aria-label="Dismiss notification"
        size="small"
      >
        <X />
      </IconButton>
    </Box>
  );
};

export const AppToaster = () => (
  <Toaster
    position="top-center"
    gutter={12}
    containerStyle={{
      top: 16,
      zIndex: 14000,
    }}
    toastOptions={{
      duration: 4200,
    }}
  />
);

export default AppToast;
